import { z } from 'zod';
import { USERNAME_MAX_LENGTH } from '../../utils/username-validation';
import {
  bigIntToNumber,
  emailValidator,
  phoneValidator,
  dateValidatorNullable,
  urlValidator,
  nonNegativeIntValidator,
} from '../common/validators';

/**
 * User photo schema
 * Contains URLs for user profile photos
 */
export const UserPhotoSchema = z.object({
  /** Full-size photo URL */
  url: z.string().url(),
  /** Thumbnail photo URL */
  thumbUrl: z.string().url(),
});

export type UserPhoto = z.infer<typeof UserPhotoSchema>;

/**
 * Base user schema with core fields
 * Used for user identification and display
 */
export const BaseUserSchema = z.object({
  /** User ID (converted from BigInt) */
  id: bigIntToNumber,
  /** Unique username (max 15 characters) */
  username: z.string().max(USERNAME_MAX_LENGTH),
  /** User email address */
  email: emailValidator,
  /** User's first name */
  firstName: z.string().nullable(),
  /** User's last name */
  lastName: z.string().nullable(),
  /** Whether user has uploaded a profile photo */
  hasPhoto: z.number().int().min(0).max(1),
  /** User trust level (0-10 scale) */
  trustLevel: nonNegativeIntValidator,
  /** User account status */
  status: z.number().int().min(0).max(255),
});

export type BaseUser = z.infer<typeof BaseUserSchema>;

/**
 * Full user schema with all fields
 * Extends base schema with additional profile information
 */
export const UserSchema = BaseUserSchema.extend({
  /** User profile photos */
  photos: UserPhotoSchema.optional(),
  /** User phone number */
  phone: phoneValidator,
  /** Whether phone number has been verified */
  isPhoneVerified: z.boolean(),
  /** Whether email address has been verified */
  isEmailVerified: z.boolean(),
  /** Shipping format preference */
  shippingFormat: z.string().max(15).nullable(),
  /** Last profile update timestamp */
  updatedDate: dateValidatorNullable,
});

export type User = z.infer<typeof UserSchema>;

/**
 * Minimal user schema for nested relations
 * Contains only essential fields for user references
 */
export const UserMinimalSchema = BaseUserSchema.pick({
  id: true,
  username: true,
  firstName: true,
  lastName: true,
});

export type UserMinimal = z.infer<typeof UserMinimalSchema>;
